import { useCallback } from 'react';

import { NSFWRoutesRedirects } from '~constants/routes';
import { useAgeConfirmationContext } from '~context/age-confirmation';
import { useAuthActionsContext } from '~context/auth';
import type {
	NsfwReason,
	RegisterInbound,
	RegisterOutbound,
} from '~features/annals';
import { annals } from '~features/annals/hooks';
import { useSession } from '~hooks/useSession';
import { usePathname, useRouter } from '~i18n/navigation';
import { useUpdateSafeModeMutation } from '~services/api/auth';

type CallbackOptions = {
	force?: boolean;
	nsfwReason: NsfwReason;
	registerInbound?: RegisterInbound;
	registerOutbound?: RegisterOutbound;
};

export const useSwitchSafeMode = () => {
	const pathname = usePathname();
	const router = useRouter();
	const { userId } = useSession();

	const [update] = useUpdateSafeModeMutation();

	const annalsSafeModeSwitched = annals.nsfw.useSafeModeSwitched();

	return useCallback(
		(isSafe: boolean, options: Pick<CallbackOptions, 'nsfwReason'>) => {
			const safeModeUpdate = () =>
				update({
					userId,
					isSafeMode: isSafe,
				})
					.unwrap()
					.then((data) => {
						annalsSafeModeSwitched({ resource: options.nsfwReason });
						if (isSafe && NSFWRoutesRedirects[pathname]) {
							router.push(NSFWRoutesRedirects[pathname]);
						}
						return data;
					});

			return new Promise((resolve, reject) => {
				safeModeUpdate().then(resolve).catch(reject);
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[update, userId, pathname]
	);
};

export const useSwitchSafeModeWithAgeValidation = () => {
	const { openAgeConfirmation } = useAgeConfirmationContext();
	const { openRegister } = useAuthActionsContext();
	const { isDemoUser } = useSession();

	const switchSafeMode = useSwitchSafeMode();

	return useCallback(
		(isSafe: boolean, options: CallbackOptions) => {
			const { force, nsfwReason, registerInbound, registerOutbound } = options;

			return new Promise((resolve, reject) => {
				const cb = () => {
					if (!isSafe) {
						openAgeConfirmation({
							callback: (isSuccess) => {
								if (isSuccess) {
									switchSafeMode(isSafe, { nsfwReason })
										.then(resolve)
										.catch(reject);
								} else {
									reject();
								}
							},
						});
					} else {
						switchSafeMode(isSafe, { nsfwReason }).then(resolve).catch(reject);
					}
				};
				if (isSafe && !isDemoUser) {
					switchSafeMode(isSafe, { nsfwReason }).then(resolve).catch(reject);
				} else if (isDemoUser && !force) {
					openRegister({
						cb,
						analytics: {
							inbound: registerInbound,
							outbound: registerOutbound,
						},
					});
				} else {
					cb();
				}
			});
		},
		[openAgeConfirmation, openRegister, isDemoUser, switchSafeMode]
	);
};
